import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { 
    Home, BookOpen, User, LogOut, ChevronRight, Menu
} from 'lucide-react';
import ThemeToggle from '@/components/ThemeToggle';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const StudentHeader = ({ breadcrumbs = [] }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { user, logout } = useAuth();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    // Ana menü linkleri
    const menuLinks = [
        { path: '/dashboard', label: 'Ana Sayfa', icon: Home },
        { path: '/profile', label: 'Profil', icon: User },
    ];

    const isActive = (path) => location.pathname === path;

    return (
        <header className="sticky top-0 z-50 border-b border-border/50 bg-background/95 backdrop-blur-md">
            <div className="max-w-6xl mx-auto px-4">
                <div className="flex items-center justify-between h-14">
                    {/* Sol: Logo ve Ana Menü */}
                    <div className="flex items-center gap-1">
                        {/* Logo / Home */}
                        <Button 
                            variant="ghost" 
                            size="sm" 
                            onClick={() => navigate('/dashboard')}
                            className="font-semibold text-primary hover:text-primary px-2"
                        >
                            <BookOpen className="w-5 h-5 mr-2" />
                            <span className="hidden sm:inline">UyumAkademi</span>
                        </Button>

                        {/* Breadcrumb - sadece varsa göster */}
                        {breadcrumbs.length > 0 && (
                            <nav className="hidden md:flex items-center text-sm text-muted-foreground">
                                {breadcrumbs.map((crumb, index) => (
                                    <React.Fragment key={index}>
                                        <ChevronRight className="w-4 h-4 mx-1" />
                                        {crumb.href ? (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => navigate(crumb.href)}
                                                className="px-2 py-1 h-auto text-muted-foreground hover:text-foreground truncate max-w-[150px]"
                                                title={crumb.label}
                                            >
                                                {crumb.label.length > 20 ? crumb.label.substring(0, 20) + '...' : crumb.label}
                                            </Button>
                                        ) : (
                                            <span className="px-2 text-foreground font-medium truncate max-w-[150px]" title={crumb.label}>
                                                {crumb.label.length > 20 ? crumb.label.substring(0, 20) + '...' : crumb.label}
                                            </span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </nav>
                        )}
                    </div>

                    {/* Sağ: Menü ve Profil */}
                    <div className="flex items-center gap-2">
                        {/* Desktop menü */}
                        <nav className="hidden sm:flex items-center gap-1">
                            {menuLinks.map((link) => (
                                <Button
                                    key={link.path}
                                    variant={isActive(link.path) ? "secondary" : "ghost"}
                                    size="sm"
                                    onClick={() => navigate(link.path)}
                                    className="px-3"
                                >
                                    <link.icon className="w-4 h-4 mr-1" />
                                    {link.label}
                                </Button>
                            ))}
                        </nav>

                        <ThemeToggle />

                        {/* Profil Dropdown */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="sm" className="px-2">
                                    <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center">
                                        <User className="w-4 h-4 text-primary" />
                                    </div>
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-48">
                                <div className="px-2 py-1.5 text-sm font-medium truncate">
                                    {user?.name || user?.email}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => navigate('/profile')}>
                                    <User className="w-4 h-4 mr-2" />
                                    Profil
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => navigate('/dashboard')}>
                                    <Home className="w-4 h-4 mr-2" />
                                    Ana Sayfa
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={handleLogout} className="text-destructive">
                                    <LogOut className="w-4 h-4 mr-2" />
                                    Çıkış Yap
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>

                        {/* Mobile menü */}
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild className="sm:hidden">
                                <Button variant="ghost" size="sm" className="px-2">
                                    <Menu className="w-5 h-5" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                                {menuLinks.map((link) => (
                                    <DropdownMenuItem key={link.path} onClick={() => navigate(link.path)}>
                                        <link.icon className="w-4 h-4 mr-2" />
                                        {link.label}
                                    </DropdownMenuItem>
                                ))}
                                {/* Mobile breadcrumb */}
                                {breadcrumbs.length > 0 && (
                                    <>
                                        <DropdownMenuSeparator />
                                        {breadcrumbs.filter(c => c.href).map((crumb, index) => (
                                            <DropdownMenuItem key={index} onClick={() => navigate(crumb.href)}>
                                                <ChevronRight className="w-4 h-4 mr-2" />
                                                {crumb.label}
                                            </DropdownMenuItem>
                                        ))}
                                    </>
                                )}
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>
        </header>
    );
};

export default StudentHeader;
